/**
 * SPEC-TrainingSimulator §7.2 — text highlights in the key editor
 * (2026-09-09, replaces bounding boxes on the text cards).
 *
 * A pointer to text on a clone card is stored as an anchor, not a box:
 * `{ field, text, occurrence }` — which card field, the exact substring,
 * and which occurrence when the string repeats (the unit "1" that is also
 * in the postal code). At render time the surface finds that range in the
 * live DOM and draws the bright-green outline around the rectangles the
 * browser reports for it, recomputed on resize, so the outline lands on
 * the text however it wraps. Photos keep normalised boxes (photo-box-panel).
 *
 * The live cards (ChecklistCard, CompletionDetailsCard) are not modified;
 * fields are located by the test ids / row labels those components render.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { BOX_STROKE } from "@/components/simulator/photo-box-panel";
import { MAX_OCCURRENCE, type HighlightField, type TextHighlight } from "@/lib/simulator-key-pointers";

export type { HighlightField, TextHighlight };
export interface HighlightLayer { kind: string; label: string; highlights: TextHighlight[] }

export const HIGHLIGHT_FIELD_LABEL: Record<HighlightField, string> = {
  company: "Company",
  recipientName: "Recipient name",
  address: "Address",
  taskNotes: "Notes",
  destinationNotes: "Destination notes",
  completionNote: "Completion note",
};

// Row labels the ChecklistCard renders (see components/qa/checklist-card.tsx).
const ROW_LABEL_TO_FIELD: Array<[string, HighlightField]> = [
  ["Company", "company"],
  ["Recipient Name", "recipientName"],
  ["Number, Street Name", "address"],
  ["Destination Notes", "destinationNotes"],
];

/** The element holding a field's value text inside a card wrapper, or null. */
export function findFieldElement(root: ParentNode, field: HighlightField): HTMLElement | null {
  if (field === "completionNote") return root.querySelector<HTMLElement>('[data-testid="completion-note"]');
  if (field === "taskNotes") {
    const row = root.querySelector<HTMLElement>('[data-testid="checklist-row-notes"]');
    return (row?.lastElementChild as HTMLElement | null) ?? null;
  }
  const wanted = ROW_LABEL_TO_FIELD.find(([, f]) => f === field)?.[0];
  if (!wanted) return null;
  for (const row of Array.from(root.querySelectorAll<HTMLElement>('[data-testid="checklist-info-row"]'))) {
    const label = (row.firstElementChild?.textContent ?? "").trim();
    if (label.startsWith(wanted)) return (row.lastElementChild as HTMLElement | null) ?? null;
  }
  return null;
}

/** Which field a DOM node sits in, walking up to the card row. */
function fieldOfNode(node: Node): { field: HighlightField; el: HTMLElement } | null {
  const el = (node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement)) ?? null;
  if (!el) return null;
  const note = el.closest<HTMLElement>('[data-testid="completion-note"]');
  if (note) return { field: "completionNote", el: note };
  const notesRow = el.closest<HTMLElement>('[data-testid="checklist-row-notes"]');
  if (notesRow) return { field: "taskNotes", el: notesRow.lastElementChild as HTMLElement };
  const row = el.closest<HTMLElement>('[data-testid="checklist-info-row"]');
  if (row) {
    const label = (row.firstElementChild?.textContent ?? "").trim();
    const hit = ROW_LABEL_TO_FIELD.find(([l]) => label.startsWith(l));
    if (hit) return { field: hit[1], el: row.lastElementChild as HTMLElement };
  }
  return null;
}

function countOccurrences(hay: string, needle: string, before: number): number {
  let n = 0;
  let i = hay.indexOf(needle);
  while (i !== -1 && i < before) { n++; i = hay.indexOf(needle, i + 1); }
  return n;
}

/**
 * Turn the current window selection into a highlight anchor. `rootIds`
 * scope the capture to the clone's text cards so a stray selection on the
 * key form is ignored. Returns an error string for the hint line instead.
 */
export function captureSelectionHighlight(rootIds: string[]): { highlight: TextHighlight } | { error: string } {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  const text = sel?.toString() ?? "";
  if (!sel || sel.rangeCount === 0 || !text.trim()) return { error: "Select some text in the Delivery Details or Completion Details card first." };
  const range = sel.getRangeAt(0);
  const roots = rootIds.map((id) => document.getElementById(id)).filter((r): r is HTMLElement => !!r);
  if (roots.length && !roots.some((r) => r.contains(range.commonAncestorContainer))) {
    return { error: "Select text inside the Delivery Details or Completion Details card." };
  }
  const hit = fieldOfNode(range.startContainer);
  if (!hit) return { error: "Select text inside one field (address, notes, destination notes or the completion note)." };
  if (!hit.el.contains(range.endContainer)) return { error: "Keep the selection inside one field." };
  const clean = text.trim();
  const fieldText = hit.el.textContent ?? "";
  // Offset of the selection start within the field text → occurrence index.
  const pre = document.createRange();
  pre.selectNodeContents(hit.el);
  pre.setEnd(range.startContainer, range.startOffset);
  const startOffset = pre.toString().length + (text.length - text.trimStart().length);
  const occurrence = countOccurrences(fieldText, clean, startOffset) + 1;
  if (!fieldText.includes(clean)) return { error: "That selection spans more than one field." };
  if (occurrence > MAX_OCCURRENCE) return { error: `That text repeats more than ${MAX_OCCURRENCE} times in the field.` };
  return { highlight: { field: hit.field, text: clean, occurrence } };
}

/** DOM range for the nth occurrence of `text` inside `el`, or null. */
function rangeFor(el: HTMLElement, text: string, occurrence: number): Range | null {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let full = "";
  const starts: number[] = [];
  while (walker.nextNode()) {
    const t = walker.currentNode as Text;
    nodes.push(t);
    starts.push(full.length);
    full += t.data;
  }
  let idx = -1;
  for (let n = 0; n < Math.max(1, occurrence); n++) {
    idx = full.indexOf(text, idx + 1);
    if (idx === -1) return null;
  }
  const end = idx + text.length;
  const locate = (pos: number, isEnd: boolean) => {
    for (let i = 0; i < nodes.length; i++) {
      const s = starts[i];
      const e = s + nodes[i].data.length;
      if (pos < e || (isEnd && pos === e) || i === nodes.length - 1) return { node: nodes[i], offset: Math.min(Math.max(pos - s, 0), nodes[i].data.length) };
    }
    return null;
  };
  const a = locate(idx, false);
  const b = locate(end, true);
  if (!a || !b) return null;
  const r = document.createRange();
  r.setStart(a.node, a.offset);
  r.setEnd(b.node, b.offset);
  return r;
}

interface Drawn { key: string; label: string; active: boolean; rects: Array<{ left: number; top: number; width: number; height: number }>; missing?: boolean }

export interface TextHighlightSurfaceProps {
  id?: string;
  layers: HighlightLayer[];
  activeKind: string | null;
  /** Restrict to these fields (the card this surface wraps). */
  fields: readonly HighlightField[];
  children: ReactNode;
}

/** Wraps a clone card and outlines every anchored highlight that lives in it. */
export function TextHighlightSurface({ id, layers, activeKind, fields, children }: TextHighlightSurfaceProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [drawn, setDrawn] = useState<Drawn[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setTick((t) => t + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const origin = root.getBoundingClientRect();
    const out: Drawn[] = [];
    for (const layer of layers) {
      layer.highlights.forEach((h, i) => {
        if (!fields.includes(h.field)) return;
        const fieldEl = findFieldElement(root, h.field);
        const range = fieldEl ? rangeFor(fieldEl, h.text, h.occurrence) : null;
        const rects = range
          ? Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0).map((r) => ({ left: r.left - origin.left, top: r.top - origin.top, width: r.width, height: r.height }))
          : [];
        out.push({ key: `${layer.kind}-${i}`, label: layer.label, active: layer.kind === activeKind, rects, missing: rects.length === 0 });
      });
    }
    setDrawn(out);
  }, [layers, activeKind, fields, tick]);

  return (
    <div id={id} ref={ref} data-testid="text-highlight-surface" style={{ position: "relative" }}>
      {children}
      {drawn.map((d) => d.rects.map((r, i) => (
        <div
          key={`${d.key}-${i}`}
          data-testid={`text-highlight-${d.key.split("-")[0]}`}
          data-active={d.active ? "true" : "false"}
          style={{
            position: "absolute", left: r.left - 3, top: r.top - 2, width: r.width + 6, height: r.height + 4, boxSizing: "border-box",
            border: `3px ${d.active ? "solid" : "dashed"} ${BOX_STROKE}`, borderRadius: 3,
            background: d.active ? `${BOX_STROKE}26` : "transparent", opacity: d.active ? 1 : 0.55, pointerEvents: "none", zIndex: 2,
          }}
        >
          {i === 0 && (
            <span style={{ position: "absolute", left: -3, top: -18, background: BOX_STROKE, color: "#111827", fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: "3px 3px 0 0", whiteSpace: "nowrap" }}>
              {d.label}
            </span>
          )}
        </div>
      )))}
    </div>
  );
}

export const DELIVERY_DETAILS_FIELDS: readonly HighlightField[] = ["company", "recipientName", "address", "taskNotes", "destinationNotes"];
export const COMPLETION_FIELDS: readonly HighlightField[] = ["completionNote"];
